import { api } from '@/config/api';

export type KnowledgeSourceType = 'url' | 'website' | 'file' | 'text' | 'faq';
export type KnowledgeStatus = 'queued' | 'processing' | 'ready' | 'failed';

export interface KnowledgeSource {
  sourceUid: string;
  type: KnowledgeSourceType;
  title: string;
  url?: string;
  maxPages?: number;
  file?: { name: string; size: number; mime: string; url?: string };
  /** Only on GET one: pasted text / Q&A answer. */
  text?: string;
  question?: string;
  status: KnowledgeStatus;
  error?: string;
  warning?: string;
  pageCount: number;
  chunkCount: number;
  charCount: number;
  embedding: { provider: string; model: string; dims: number } | null;
  lastSyncedAt?: string;
  nextSyncAt?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface KnowledgeSummary {
  sources: number;
  ready: number;
  working: number;
  failed: number;
  keywordOnly: number;
  chunks: number;
  openGaps: number;
  embeddingRoute: { provider: string; model: string } | null;
  limits: { sources: number; pagesPerSite: number; chunksPerCompany: number; fileBytes: number };
}

export interface KnowledgeHit { n: number; sourceUid: string; title: string; url?: string; text: string; score: number }

export interface KnowledgeGap {
  gapUid: string;
  question: string;
  examples: string[];
  count: number;
  lastAskedAt?: string;
  channel?: string;
  conversationUid?: string;
  status: 'open' | 'answered' | 'dismissed';
  answerSourceUid?: string;
}

const BASE = '/slighter_crm/knowledge';

export const knowledgeService = {
  summary: () => api.get<KnowledgeSummary>(`${BASE}/summary`).then((r) => r.data),
  sources: () => api.get<KnowledgeSource[]>(`${BASE}/sources`).then((r) => r.data),
  source: (uid: string) => api.get<KnowledgeSource>(`${BASE}/sources/${uid}`).then((r) => r.data),
  chunks: (uid: string) => api.get<Array<{ idx: number; title: string; url?: string; text: string }>>(`${BASE}/sources/${uid}/chunks`).then((r) => r.data),
  addUrl: (body: { url: string; title?: string }) => api.post<KnowledgeSource>(`${BASE}/sources/url`, body).then((r) => r.data),
  addWebsite: (body: { url: string; maxPages?: number; title?: string }) => api.post<KnowledgeSource>(`${BASE}/sources/website`, body).then((r) => r.data),
  addText: (body: { title: string; text: string }) => api.post<KnowledgeSource>(`${BASE}/sources/text`, body).then((r) => r.data),
  addFaq: (body: { question: string; answer: string }) => api.post<KnowledgeSource>(`${BASE}/sources/faq`, body).then((r) => r.data),
  addFile: (file: File, title?: string) => {
    const form = new FormData();
    form.append('file', file);
    if (title) form.append('title', title);
    return api.post<KnowledgeSource>(`${BASE}/sources/file`, form, { headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data);
  },
  update: (uid: string, body: { title?: string; text?: string; question?: string; answer?: string }) => api.patch<KnowledgeSource>(`${BASE}/sources/${uid}`, body).then((r) => r.data),
  resync: (uid: string) => api.post<KnowledgeSource>(`${BASE}/sources/${uid}/resync`).then((r) => r.data),
  remove: (uid: string) => api.delete<{ ok: boolean }>(`${BASE}/sources/${uid}`).then((r) => r.data),
  search: (query: string, sourceUids?: string[]) => api.post<KnowledgeHit[]>(`${BASE}/search`, { query, ...(sourceUids?.length ? { sourceUids } : {}) }).then((r) => r.data),
  gaps: (status: KnowledgeGap['status'] = 'open') => api.get<KnowledgeGap[]>(`${BASE}/gaps`, { params: { status } }).then((r) => r.data),
  answerGap: (uid: string, answer: string, question?: string) => api.post(`${BASE}/gaps/${uid}/answer`, { answer, ...(question ? { question } : {}) }).then((r) => r.data),
  setGapStatus: (uid: string, status: 'open' | 'dismissed') => api.post(`${BASE}/gaps/${uid}/status`, { status }).then((r) => r.data),
};
